import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {CS_COUNTRY_VS_WF_COLORS, POM_COUNTRY_VS_WF_COLORS} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

interface CountryWeeeSystemData {
  kind: string;
  countryData: CountryChartData[];
}

@Component({
  selector: 'app-country-vs-wf-bar-chart',
  templateUrl: './country-vs-wf-bar-chart.component.html',
  styleUrls: ['./country-vs-wf-bar-chart.component.scss']
})
export class CountryVsWfBarChartComponent implements OnInit, OnChanges {

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryTextBelowCodePOM = 'kfcharts.Country.PutOnMarket.CountryVsWeeeForum.textBelow';
  countryTextBelowCodeCollected = 'kfcharts.Country.Collected.CountryVsWeeeForum.textBelow';
  weeeSystemTextBelowCodePOM = 'kfcharts.WeeeSystem.PutOnMarket.CountryVsWeeeForum.textBelow';
  weeeSystemTextBelowCodeCollected = 'kfcharts.WeeeSystem.Collected.CountryVsWeeeForum.textBelow';
  isAdmin!: boolean;

  @Input()
  data!: CountryChartData[];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  countryCategory!: string;
  @Input()
  country!: boolean;
  @Input()
  countryCodes!: string[];

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    if (this.country) {
      this.messageService.getDialogInfo(
        this.countryCategory === 'putonmarket' ? this.countryTextBelowCodePOM : this.countryTextBelowCodeCollected).subscribe((data) => {
        if (data !== null) {
          this.textBelow = data.description;
        }
      });
    } else {
      this.messageService.getDialogInfo(
        this.countryCategory === 'putonmarket' ? this.countryTextBelowCodePOM : this.countryTextBelowCodeCollected).subscribe((data) => {
        if (data !== null) {
          this.textBelow = data.description;
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.data && this.countryCodes) {
      // this.countryCodes = CountryVsWfBarChartComponent.getCountryCodes(this.data);
      // if (!this.router.url.includes('order=')) {
      //   this.countryCodes.sort();
      // }
      const normalizedData = this.normalizeData(this.groupByDataKind(this.data));

      // highcharts
      this.chartData = this.convertToChartData(normalizedData);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: this.countryCategory === 'putonmarket' ? POM_COUNTRY_VS_WF_COLORS : CS_COUNTRY_VS_WF_COLORS,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        tooltip: {
          formatter(): string[] | undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                // return [this.point.name];
              }
              return ['<div class="center"><b>' + this.point.name + '</b></div><br><br>',
                '- Absolute: ' + (pointOptions.custom?.absolute === 0 ? 'No data' : formatNumber(pointOptions.custom?.absolute) + ' tons') + '<br>',
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {
        state: {
          code: this.country ?
            (this.countryCategory === 'putonmarket' ? this.countryTextBelowCodePOM : this.countryTextBelowCodeCollected) :
            (this.countryCategory === 'putonmarket' ? this.weeeSystemTextBelowCodePOM : this.weeeSystemTextBelowCodeCollected)
        }
      }).then(
      () => {
        // do nothing
      }
    );
  }

  private normalizeData(groupedByDataKind: CountryWeeeSystemData[]): CountryWeeeSystemData[] {
    const normalizedData: CountryWeeeSystemData[] = [];
    groupedByDataKind.forEach(item => {
      const newCountryData: CountryChartData[] = [];
      this.countryCodes.forEach(code => {
        let countryDataItem = item.countryData.find(c => c.countryCode === code);
        if (!countryDataItem) {
          countryDataItem = {
            countryCode: code,
            absolute: 0,
            year: groupedByDataKind[0].countryData[0].year,
            population: 0,
            countryName: groupedByDataKind[0].countryData.find(c => c.countryCode === code)?.countryName ||
              groupedByDataKind[1].countryData.find(c => c.countryCode === code)?.countryName
          } as CountryChartData;
        }
        newCountryData.push(countryDataItem);
      });
      normalizedData.push({kind: item.kind, countryData: newCountryData});
    });
    return normalizedData;
  }

  private convertToChartData(data: CountryWeeeSystemData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    data.forEach(item => {
      if (!this.router.url.includes('order=')) {
        item.countryData.sort((a, b) => {
          if (a.countryCode < b.countryCode) {
            return -1;
          }
          if (a.countryCode > b.countryCode) {
            return 1;
          }
          return 0;
        });
      }
      chartData.push({
        name: item.kind,
        type: 'column',
        data: item.countryData.map(dataItem => {
          return {
            y: (this.displayUnits === 'SPECIFIC' ? (dataItem.absolute * 1000 / dataItem.population) : dataItem.absolute),
            name: dataItem.countryName + ' - ' + dataItem.year,
            custom: {absolute: dataItem.absolute ? dataItem.absolute : 0}
          };
        })
      });
    });
    return chartData;
  }

  private groupByDataKind(objectArray: Array<any>): CountryWeeeSystemData[] {
    const yearCountryDataArray: CountryWeeeSystemData[] = [];
    const groupedArray = objectArray.reduce((acc, obj) => {
      let key;
      if (obj.countryOrWeeeForum === 'Country' && this.countryCategory === 'putonmarket') {
        key = 'Total Country POM';
      } else if (obj.countryOrWeeeForum === 'Country' && this.countryCategory === 'collected') {
        key = 'Total country Collection';
      } else if (obj.countryOrWeeeForum === 'Weee Forum' && this.countryCategory === 'putonmarket') {
        key = 'POM WEEE Forum members';
      } else if (obj.countryOrWeeeForum === 'Weee Forum' && this.countryCategory === 'putonmarket') {
        key = 'Collection WEEE Forum members';
      } else {
        if (this.countryCategory === 'putonmarket') {
          key = 'Put On Market WEEE Forum members';
        } else {
          key = 'Collection WEEE Forum members';
        }
        // key = obj.countryOrWeeeForum;
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      // Add object to list for given key's value
      acc[key].push(obj);
      return acc;
    }, {});

    Object.getOwnPropertyNames(groupedArray).forEach((key: string) => {
      const countryWeeeSystemData = {} as CountryWeeeSystemData;
      countryWeeeSystemData.kind = key;
      countryWeeeSystemData.countryData = groupedArray[key];
      yearCountryDataArray.push(countryWeeeSystemData);
    });

    return yearCountryDataArray;
  }
}
